import React, {
	Component,
	type ComponentType,
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';

import PropTypes from 'prop-types';

import type { MaybeChoreographedComponentProps } from '@atlassian/ipm-choreographer/factory/types';

import {
	type CurrentMessage,
	type ComponentType as EngagementComponentType,
} from '../../common/types/message';
import { type State as EngagementStoreState } from '../engagement-store';

type State = {
	message: CurrentMessage | null;
};

type Context = {
	subscribeEngagementState:
		| ((cb: (state: EngagementStoreState) => void) => (() => void) | void)
		| null;
	setComponentConfig:
		| ((
				componentType: EngagementComponentType,
				config: any,
				engagementId?: string,
		  ) => (() => void) | void)
		| null;
};

export type Props = {
	engagementId?: string;
	children?: ReactNode;
} & MaybeChoreographedComponentProps;

export default (
	InnerComponent: ComponentType<any>,
	componentType: EngagementComponentType,
): ComponentType<any> => {
	function isTargetComponent(message: CurrentMessage): boolean {
		return message.componentType === componentType;
	}

	function computeNewState(state: EngagementStoreState): State {
		const current = state.current;
		if (current && isTargetComponent(current)) {
			let message;
			switch (current.componentType) {
				case 'contextualSurvey':
					message = {
						...current,
						aaid: state.config.aaid,
						cloudId: state.config.cloudId,
						stargateUrl: state.config.stargateUrl,
						product: state.config.product,
						orgId: state.config.orgId,
						workspaceId: state.config.workspaceId,
					};
					break;

				default:
					message = current;
			}

			return {
				message,
			};
		}

		return { message: null };
	}

	function getComponentType(): EngagementComponentType {
		return componentType;
	}

	// eslint-disable-next-line @repo/internal/react/no-class-components
	class StoreWrapper extends Component<Props, State> {
		context: any;
		static contextTypes = {
			subscribeEngagementState: PropTypes.func,
			setComponentConfig: PropTypes.func,
		};

		render() {
			return <StoreWrapperInner {...this.props} context={this.context} />;
		}
	}

	const StoreWrapperInner: React.FC<Props & { context: Context }> = (allProps) => {
		const { context, ...props } = allProps;
		const { children, engagementId, messageType, onMessageDisposition } = props;
		const [message, setMessage] = useState<CurrentMessage | null>(null);

		const unsubscribeRef = useRef<(() => void) | null>(null);
		const removeConfigRef = useRef<(() => void) | null>(null);

		const canSubscribe = useCallback((): boolean => {
			return context && !!context?.subscribeEngagementState;
		}, [context]);

		const canSetConfig = useCallback((): boolean => {
			return context && !!context?.setComponentConfig;
		}, [context]);

		const onStoreChange = useCallback((state: EngagementStoreState) => {
			setMessage(computeNewState(state).message);
		}, []);

		const subscribeToStore = useCallback(() => {
			return context?.subscribeEngagementState?.(onStoreChange);
		}, [context, onStoreChange]);

		const setChoreographerConfig = useCallback(
			(propsToUse: Props = props) => {
				return context?.setComponentConfig?.(
					getComponentType(),
					{
						messageType: propsToUse.messageType,
						onMessageDisposition: propsToUse.onMessageDisposition,
					},
					propsToUse.engagementId,
				);
			},
			[context, props],
		);

		// Handle initial mount (equivalent to UNSAFE_componentWillMount)
		useEffect(() => {
			if (canSubscribe()) {
				unsubscribeRef.current = subscribeToStore() || null;
			}
			if (canSetConfig()) {
				removeConfigRef.current = setChoreographerConfig() || null;
			}

			return () => {
				if (unsubscribeRef.current) {
					unsubscribeRef.current();
				}
				if (removeConfigRef.current) {
					removeConfigRef.current();
				}
			};
		}, [canSubscribe, canSetConfig, subscribeToStore, setChoreographerConfig]);

		// Handle prop changes (equivalent to UNSAFE_componentWillReceiveProps)
		const previousEngagementId = useRef<Props['engagementId']>(engagementId);
		const previousMessageType = useRef<Props['messageType']>(messageType);
		const previousOnMessageDisposition =
			useRef<Props['onMessageDisposition']>(onMessageDisposition);

		useEffect(() => {
			if (
				previousEngagementId.current !== engagementId &&
				removeConfigRef.current &&
				canSetConfig()
			) {
				removeConfigRef.current();
				removeConfigRef.current = setChoreographerConfig(props) || null;
			} else if (
				(previousMessageType.current !== messageType ||
					previousOnMessageDisposition.current !== onMessageDisposition) &&
				canSetConfig()
			) {
				setChoreographerConfig(props);
			}

			previousEngagementId.current = engagementId;
			previousMessageType.current = messageType;
			previousOnMessageDisposition.current = onMessageDisposition;
		}, [
			props,
			setChoreographerConfig,
			canSetConfig,
			engagementId,
			messageType,
			onMessageDisposition,
		]);

		if (!message) {
			if (componentType === 'inlineDialog') {
				return children || null;
			}

			return null;
		}

		if (engagementId && message !== null && message.target && engagementId !== message.target) {
			return null;
		}

		const messageKey = `${message.messageId}.${message.variationId}.${message.componentId}`;

		// Key is useful here to make sure that when the currently active
		// messages changes, the old component is unmounted (therefore
		// firing an analytic event for hiding the component) and mounting
		// a new component for the new message (firing a shown event for
		// the new message). Not using key would allow React to apply an
		// optimization that would reuse the current component for a new
		// message which would not call the normal mount and unmount
		// lifecycle methods.
		return <InnerComponent key={messageKey} {...props} {...message} />;
	};

	return StoreWrapper;
};
